import { BookMarked, FileStack, Printer, ScrollText, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import type { BookExportSummary, BookExportSummarySection } from "@/features/book-assembly/book-assembly.types";

const sectionLabelKeys: Record<BookExportSummarySection["key"], string> = {
  characters_appendix: "bookAssembly.summary.sectionLabels.characters_appendix",
  cover: "bookAssembly.summary.sectionLabels.cover",
  end_matter: "bookAssembly.summary.sectionLabels.end_matter",
  lore_appendix: "bookAssembly.summary.sectionLabels.lore_appendix",
  main_story: "bookAssembly.summary.sectionLabels.main_story",
  side_stories: "bookAssembly.summary.sectionLabels.side_stories",
  table_of_contents: "bookAssembly.summary.sectionLabels.table_of_contents",
  timeline_appendix: "bookAssembly.summary.sectionLabels.timeline_appendix",
  title_page: "bookAssembly.summary.sectionLabels.title_page",
};

const warningLabelKeys: Record<string, string> = {
  missing_cover_art: "bookAssembly.summary.warningLabels.missing_cover_art",
  no_appendices_selected: "bookAssembly.summary.warningLabels.no_appendices_selected",
  no_side_stories_selected: "bookAssembly.summary.warningLabels.no_side_stories_selected",
};

function getTargetLabelKey(target: BookExportSummary["target"]) {
  switch (target) {
    case "docx":
      return "bookAssembly.summary.targets.docx";
    case "both":
      return "bookAssembly.summary.targets.both";
    default:
      return "bookAssembly.summary.targets.pdf";
  }
}

function getModeLabelKey(mode: BookExportSummary["exportMode"]) {
  switch (mode) {
    case "full_compendium":
      return "bookAssembly.summary.modes.full_compendium";
    case "side_stories_only":
      return "bookAssembly.summary.modes.side_stories_only";
    default:
      return "bookAssembly.summary.modes.story_only";
  }
}

function getPresetLabelKey(preset: BookExportSummary["preset"]) {
  return `bookAssembly.summary.presets.${preset}`;
}

function getPageSizeLabelKey(pageSize: BookExportSummary["pageSize"]) {
  return `bookAssembly.summary.pageSizes.${pageSize}`;
}

export function BookExportSummaryCard({
  summary,
}: {
  summary: BookExportSummary | null | undefined;
}) {
  const { t } = useTranslation();

  if (!summary) {
    return null;
  }

  return (
    <section
      className="space-y-6 border-b border-ink/10 pb-6"
      data-testid="book-export-summary"
    >
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-clay">
            {t("bookAssembly.summary.eyebrow")}
          </p>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <h3 className="text-2xl text-foreground">{t("bookAssembly.summary.title")}</h3>
              <p className="text-sm leading-7 text-muted-foreground">
                {t("bookAssembly.summary.body")}
              </p>
            </div>
            <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground sm:flex">
              <BookMarked className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="border-t border-ink/10 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {t("bookAssembly.summary.target")}
            </p>
            <p className="mt-2 text-lg font-semibold text-foreground">
              <span data-testid="book-export-summary-target">
                {t(getTargetLabelKey(summary.target))}
              </span>
            </p>
          </div>
          <div className="border-t border-ink/10 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {t("bookAssembly.summary.mode")}
            </p>
            <p className="mt-2 text-lg font-semibold text-foreground">
              <span data-testid="book-export-summary-mode">
                {t(getModeLabelKey(summary.exportMode))}
              </span>
            </p>
          </div>
          <div className="border-t border-ink/10 py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <ScrollText className="h-4 w-4" />
              <p className="text-xs font-semibold uppercase tracking-[0.18em]">
                {t("bookAssembly.summary.preset")}
              </p>
            </div>
            <p className="mt-2 text-lg font-semibold text-foreground">
              <span data-testid="book-export-summary-preset">
                {t(getPresetLabelKey(summary.preset))}
              </span>
            </p>
          </div>
          <div className="border-t border-ink/10 py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Printer className="h-4 w-4" />
              <p className="text-xs font-semibold uppercase tracking-[0.18em]">
                {t("bookAssembly.summary.pageSize")}
              </p>
            </div>
            <p className="mt-2 text-lg font-semibold text-foreground">
              <span data-testid="book-export-summary-page-size">
                {t(getPageSizeLabelKey(summary.pageSize))}
              </span>
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <FileStack className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {t("bookAssembly.summary.sectionTitle")}
            </p>
          </div>
          <div className="space-y-2">
            {summary.sections.map((section) => (
              <div
                className="flex items-center justify-between gap-3 border-t border-ink/10 py-3 text-sm first:border-t-0"
                data-testid={`book-export-summary-section-${section.key}`}
                key={section.key}
              >
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    {t(sectionLabelKeys[section.key])}
                  </p>
                  <p className="text-muted-foreground">
                    {section.included
                      ? t("bookAssembly.summary.includedCount", { count: section.itemCount })
                      : t("bookAssembly.summary.omitted")}
                  </p>
                </div>
                <Badge variant={section.included ? "default" : "secondary"}>
                  {section.included
                    ? t("bookAssembly.common.included")
                    : t("bookAssembly.common.excluded")}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3 border-t border-amber-600/20 pt-4">
          <div className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 text-amber-700" />
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-900">
              {t("bookAssembly.summary.warningTitle")}
            </p>
          </div>
          {summary.warnings.length > 0 ? (
            <ul className="space-y-2 text-sm text-amber-950">
              {summary.warnings.map((warning) => (
                <li key={warning}>{t(warningLabelKeys[warning] ?? warning)}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-amber-950">{t("bookAssembly.summary.noWarnings")}</p>
          )}
        </div>
    </section>
  );
}
